import {
  ENTITY_TABLES,
  type CachedEntity,
  type CompositeCacheEntry,
  type EntityType,
  type IdMapping,
  type OfflineStorage,
  type SyncQueueItem,
} from '@coachapp/offline-core';
import { type EntityTable } from 'dexie';
import { db } from './db';

function tableFor(entity: EntityType): EntityTable<CachedEntity, 'id'> | null {
  switch (entity) {
    case 'clients':
      return db.clients;
    case 'bookings':
      return db.bookings;
    case 'tasks':
      return db.tasks;
    case 'leads':
      return db.leads;
    case 'measurements':
      return db.measurements;
    case 'packages':
      return db.packages;
    case 'challenges':
      return db.challenges;
    case 'trainingPlans':
      return db.trainingPlans;
    default:
      return null;
  }
}

export const dexieStorage: OfflineStorage = {
  async getMeta(key: string): Promise<string | null> {
    const entry = await db.meta.get(key);
    return entry?.value ?? null;
  },

  async setMeta(key: string, value: string): Promise<void> {
    await db.meta.put({ key, value });
  },

  isEntityTable(entity: EntityType): boolean {
    return ENTITY_TABLES.includes(entity as (typeof ENTITY_TABLES)[number]);
  },

  async getEntityRecord(entity: EntityType, id: string): Promise<CachedEntity | undefined> {
    const table = tableFor(entity);
    if (!table) return undefined;
    return table.get(id);
  },

  async putEntityRecord(entity: EntityType, record: CachedEntity): Promise<void> {
    const table = tableFor(entity);
    if (!table) return;
    await table.put(record);
  },

  async bulkPutEntityRecords(entity: EntityType, records: CachedEntity[]): Promise<void> {
    const table = tableFor(entity);
    if (!table || records.length === 0) return;
    await table.bulkPut(records);
  },

  async deleteEntityRecord(entity: EntityType, id: string): Promise<void> {
    const table = tableFor(entity);
    if (!table) return;
    await table.delete(id);
  },

  async listEntityRecords(entity: EntityType): Promise<CachedEntity[]> {
    const table = tableFor(entity);
    if (!table) return [];
    return table.toArray();
  },

  async getCompositeEntry(key: string): Promise<CompositeCacheEntry | undefined> {
    return db.compositeCache.get(key);
  },

  async putCompositeEntry(entry: CompositeCacheEntry): Promise<void> {
    await db.compositeCache.put(entry);
  },

  async deleteCompositeEntry(key: string): Promise<void> {
    await db.compositeCache.delete(key);
  },

  async countQueueItems(): Promise<number> {
    return db.syncQueue.count();
  },

  async listQueueItems(): Promise<SyncQueueItem[]> {
    return db.syncQueue.orderBy('createdAt').toArray();
  },

  async addQueueItem(item: SyncQueueItem): Promise<void> {
    await db.syncQueue.add(item);
  },

  async getQueueItem(id: string): Promise<SyncQueueItem | undefined> {
    return db.syncQueue.get(id);
  },

  async updateQueueItem(item: SyncQueueItem): Promise<void> {
    await db.syncQueue.put(item);
  },

  async deleteQueueItem(id: string): Promise<void> {
    await db.syncQueue.delete(id);
  },

  async clearQueueItems(): Promise<void> {
    await db.syncQueue.clear();
  },

  async putIdMapping(mapping: IdMapping): Promise<void> {
    await db.idMappings.put(mapping);
  },

  async getIdMapping(tempId: string): Promise<IdMapping | undefined> {
    return db.idMappings.get(tempId);
  },

  async listIdMappingsByEntity(entityType: EntityType): Promise<IdMapping[]> {
    return db.idMappings.where('entityType').equals(entityType).toArray();
  },

  async clearAll(): Promise<void> {
    await db.transaction(
      'rw',
      [
        db.clients,
        db.bookings,
        db.tasks,
        db.leads,
        db.measurements,
        db.packages,
        db.challenges,
        db.trainingPlans,
        db.syncQueue,
        db.idMappings,
        db.compositeCache,
        db.meta,
      ],
      async () => {
        await Promise.all([
          db.clients.clear(),
          db.bookings.clear(),
          db.tasks.clear(),
          db.leads.clear(),
          db.measurements.clear(),
          db.packages.clear(),
          db.challenges.clear(),
          db.trainingPlans.clear(),
          db.syncQueue.clear(),
          db.idMappings.clear(),
          db.compositeCache.clear(),
          db.meta.clear(),
        ]);
      },
    );
  },
};
